import { useCallback, useRef } from 'react';
import { signInWithCustomToken, signOut } from 'firebase/auth';
import { ref, onValue, get, set, DataSnapshot } from 'firebase/database';
import { chatAuth, chatDb, isChatConfigured } from 'src/config/firebaseChat.config';
import { dispatch, useSelector, AppState } from 'src/store/Store';
import { chatApis } from 'src/services/chat';
import {
  setUnreadMessages,
  clearRoomUnread,
  clearInboxRoomUnread,
  setInbox,
  setSignedIn,
  reset as resetMessages,
} from 'src/store/apps/messages/MessagesSlice';
import { InboxRow } from 'src/types/apps/messages';

let signInPromise: Promise<void> | null = null;
let signInPromiseUid: string | null = null;

const attemptSignIn = async (): Promise<void> => {
  const result = await dispatch(chatApis.endpoints.getChatToken.initiate()).unwrap();
  await signInWithCustomToken(chatAuth!, result.token);
};

const isCurrentUser = (expectedUid?: string): boolean =>
  !!chatAuth?.currentUser && (expectedUid == null || chatAuth.currentUser.uid === expectedUid);

// Ensures the dashboard is signed into the `malaeb-chat` Firebase Auth instance.
// Exported standalone (not just via the hook below) so ChatDrawer/useChatDetails
// can guarantee sign-in before touching RTDB without duplicating this flow.
// Retries once on failure (custom-token/auth error) before giving up (PRD §9).
export const ensureChatSignIn = async (expectedUid?: string | number): Promise<void> => {
  if (!isChatConfigured || !chatAuth) return;

  const expected = expectedUid != null ? String(expectedUid) : undefined;

  // Account switch: if a different Firebase user is still signed in, force
  // re-auth as the current dashboard user (Bugbot: stale auth on switch).
  if (chatAuth.currentUser && !isCurrentUser(expected)) {
    await signOut(chatAuth).catch(() => undefined);
  } else if (isCurrentUser(expected)) {
    dispatch(setSignedIn(true));
    return;
  }

  // Reuse an in-flight sign-in only when it is for the same dashboard user.
  // A shared promise from a previous account must not be returned after switch.
  if (signInPromise && (expected == null || signInPromiseUid == null || signInPromiseUid === expected)) {
    return signInPromise;
  }

  if (signInPromise) {
    await signInPromise.catch(() => undefined);
    if (isCurrentUser(expected)) {
      dispatch(setSignedIn(true));
      return;
    }
    if (chatAuth.currentUser) {
      await signOut(chatAuth).catch(() => undefined);
    }
  }

  signInPromiseUid = expected ?? null;
  signInPromise = (async () => {
    try {
      try {
        await attemptSignIn();
      } catch {
        await attemptSignIn();
      }
      if (expected && chatAuth.currentUser && chatAuth.currentUser.uid !== expected) {
        // Fail closed (Bugbot: auth mismatch fails open) — the outer catch
        // below dispatches setSignedIn(false) and rejects, so callers that
        // await this can't mistake a mismatched sign-in for a successful one.
        await signOut(chatAuth).catch(() => undefined);
        throw new Error('Chat auth resolved to an unexpected user');
      }
      dispatch(setSignedIn(true));
    } catch (error) {
      dispatch(setSignedIn(false));
      throw error;
    } finally {
      signInPromise = null;
      signInPromiseUid = null;
    }
  })();

  return signInPromise;
};

// Mirrors malaeb-player MessagesDetailsHook.setReadMessages + resetInboxUnread
// + clearRoomUnread. Safe to call from ChatDrawer open/close and inbox row click.
export const markChatAsRead = async (chatId: string, uid: string | number): Promise<void> => {
  if (!chatDb || !chatId || uid == null) return;

  const roomId = String(chatId);
  const userId = String(uid);

  await ensureChatSignIn(userId);

  await Promise.all([
    set(ref(chatDb, `users/${userId}/unreads_rooms/${roomId}`), 0),
    set(ref(chatDb, `user-chat-inbox/${userId}/${roomId}/unread_count`), 0),
  ]);

  // Only after auth + RTDB writes succeed — otherwise Redux can show zero
  // while Firebase still has a count, and the live listener will not
  // correct it until a later unread write arrives.
  dispatch(clearRoomUnread(roomId));
  dispatch(clearInboxRoomUnread(roomId));
};

const parseStadiumId = (value: unknown): number | undefined => {
  if (value == null || value === '') return undefined;
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : undefined;
};

// A conversation opened from Match Players (or created on the fly) may not
// be in the inbox snapshot yet. stadium_id still lives on the per-room
// inbox node and on chat attributes — read those directly.
export const readRoomStadiumId = async (
  chatId: string,
  uid: string | number,
): Promise<number | undefined> => {
  if (!chatDb || !chatId || uid == null) return undefined;

  const userId = String(uid);
  const roomId = String(chatId);

  try {
    const inboxSnap = await get(ref(chatDb, `user-chat-inbox/${userId}/${roomId}/stadium_id`));
    const fromInbox = parseStadiumId(inboxSnap.val());
    if (fromInbox != null) return fromInbox;

    const attrSnap = await get(ref(chatDb, `chats/${roomId}/attributes/stadium_id`));
    return parseStadiumId(attrSnap.val());
  } catch {
    return undefined;
  }
};

const mapInboxRow = (chatId: string, val: any): InboxRow | null => {
  if (!val) return null;

  return {
    chatId,
    type: val.type,
    targetId: String(val.target_id ?? ''),
    targetName: val.target_name || '',
    targetNameAr: val.target_name_ar || undefined,
    lastMessage: val.last_message ?? null,
    lastMessageImage: val.last_message_image ?? null,
    lastTime: val.last_time ?? 0,
    unreadCount: val.unread_count ?? 0,
    stadiumId: val.stadium_id != null ? Number(val.stadium_id) : undefined,
  };
};

const useFirebaseMessages = () => {
  const uid = useSelector((state: AppState) => state.authReducer.user?.data?.id);
  const unreadUnsubRef = useRef<(() => void) | null>(null);
  const inboxUnsubRef = useRef<(() => void) | null>(null);

  const signIn = useCallback(async () => {
    await ensureChatSignIn(uid);
  }, [uid]);

  // Live map of `users/{uid}/unreads_rooms`. Badges combine this with inbox
  // `unread_count` via `getRoomUnreadCount` / `sumUnreadMessagesForStadium`
  // (`Math.max(live, inbox)`) so a lagging live map still shows a count.
  const subscribeUnreads = useCallback(() => {
    if (!chatDb || !uid) return () => undefined;

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    ensureChatSignIn(uid)
      .then(() => {
        if (cancelled || !chatDb) return;

        const unreadsRef = ref(chatDb, `users/${String(uid)}/unreads_rooms`);
        unsubscribe = onValue(
          unreadsRef,
          (snapshot: DataSnapshot) => {
            const raw = snapshot.val();
            if (!raw || typeof raw !== 'object') {
              dispatch(setUnreadMessages({}));

              return;
            }

            // Keep only rooms with a positive unread count. Zeros (left behind by
            // markChatAsRead / set(0)) must not inflate the topbar badge — once a
            // chat is opened it should disappear from this map, matching player UX
            // after MessagesDetailsHook clears the room.
            const normalized: Record<string, number> = {};
            for (const key of Object.keys(raw)) {
              const value = Number(raw[key]);
              if (Number.isFinite(value) && value > 0) normalized[key] = value;
            }
            dispatch(setUnreadMessages(normalized));
          },
          () => {
            // Fail silently — topbar icon still renders with no badge (PRD §9).
          },
        );

        unreadUnsubRef.current = unsubscribe;
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [uid]);

  const subscribeInbox = useCallback(() => {
    if (!chatDb || !uid) return () => undefined;

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    // Wait for chat auth so the first attach is not rejected by RTDB rules
    // (Bugbot: inbox listens before chat auth).
    ensureChatSignIn(uid)
      .then(() => {
        if (cancelled || !chatDb) return;

        // Keep the full inbox in Redux. Stadium scoping and search happen
        // in the Chats page / badge helpers — a client-side page cap was
        // silently dropping older venue chats (Bugbot: inbox drops older chats).
        const inboxRef = ref(chatDb, `user-chat-inbox/${String(uid)}`);

        unsubscribe = onValue(
          inboxRef,
          (snapshot: DataSnapshot) => {
            const rows: InboxRow[] = [];
            snapshot.forEach((child) => {
              const mapped = mapInboxRow(child.key as string, child.val());
              if (mapped) rows.push(mapped);

              return false;
            });

            dispatch(setInbox(rows.sort((a, b) => b.lastTime - a.lastTime)));
          },
          () => {
            // Fail silently — inbox stays empty rather than crashing the drawer.
          },
        );

        inboxUnsubRef.current = unsubscribe;
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [uid]);

  const signOutChat = useCallback(async () => {
    unreadUnsubRef.current?.();
    inboxUnsubRef.current?.();
    unreadUnsubRef.current = null;
    inboxUnsubRef.current = null;
    dispatch(resetMessages());
    if (chatAuth?.currentUser) {
      await signOut(chatAuth).catch(() => undefined);
    }
  }, []);

  return {
    signIn,
    subscribeUnreads,
    subscribeInbox,
    signOutChat,
  };
};

export default useFirebaseMessages;
